import { motion } from "framer-motion";
import { DotGrid, SceneShell, TerminalLine } from "./course-explainer";

type SceneProps = { reduceMotion: boolean };

export type ExplainerScript = {
  kicker: string;
  aria: string;
  scenes: {
    durationMs?: number;
    render: React.FC<SceneProps>;
  }[];
};

// =============================================================
// All About AI — 5 scenes
// =============================================================

const AiScene1: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="01 · Computational systems"
    title={
      <>
        Rules vs.{" "}
        <span className="font-italic-serif text-secondary">probability.</span>
      </>
    }
    body="Traditional programs follow exact rules. AI systems learn patterns from data and reason under uncertainty."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 grid grid-rows-2 gap-3">
      {/* Deterministic side */}
      <motion.div
        className="rounded-xl border border-background/15 bg-background/[0.04] p-4 font-mono text-xs text-background/80"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.3 }}
      >
        <div className="text-secondary/80 mb-1">// deterministic</div>
        <div>if temp &gt; 30:</div>
        <div className="pl-4">return "hot"</div>
        <div>else:</div>
        <div className="pl-4">return "cold"</div>
      </motion.div>
      {/* Probabilistic side */}
      <motion.div
        className="relative rounded-xl border border-background/15 bg-background/[0.04] p-4 overflow-hidden"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.55 }}
      >
        <div className="font-mono text-xs text-secondary/80 mb-2">
          // probabilistic
        </div>
        <div className="space-y-1.5">
          {[
            { label: "hot", w: 0.62 },
            { label: "warm", w: 0.27 },
            { label: "cold", w: 0.11 },
          ].map((b, i) => (
            <div key={b.label} className="flex items-center gap-2 font-mono text-[11px]">
              <span className="w-10 text-background/70">{b.label}</span>
              <div className="flex-1 h-1.5 bg-background/15 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-secondary"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: b.w }}
                  transition={{
                    duration: 0.8,
                    delay: reduceMotion ? 0 : 0.8 + i * 0.12,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{ transformOrigin: "left" }}
                />
              </div>
              <span className="w-10 text-right tabular-nums text-background/60">
                {Math.round(b.w * 100)}%
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </SceneShell>
);

const AiScene2: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="02 · Data & representation"
    title={
      <>
        Everything becomes{" "}
        <span className="font-italic-serif text-secondary">numbers.</span>
      </>
    }
    body="Pixels, words, sounds — every input gets reshaped into features a model can read."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 rounded-xl border border-background/15 bg-background/[0.04] overflow-hidden">
      <DotGrid rows={6} cols={9} reduceMotion={reduceMotion} />
    </div>
  </SceneShell>
);

const AiScene3: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="03 · Models & neural nets"
    title={
      <>
        A network of{" "}
        <span className="font-italic-serif text-secondary">decisions.</span>
      </>
    }
    body="Weights and activations pass signals through layers. Training nudges them until predictions improve."
    reduceMotion={reduceMotion}
  >
    {/* Tiny neural net diagram */}
    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
      {(() => {
        const cols = [
          [40, [40, 100, 160]],
          [100, [30, 80, 120, 170]],
          [160, [70, 130]],
        ] as [number, number[]][];
        const edges: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];
        for (let i = 0; i < cols.length - 1; i++) {
          const [x1, ys1] = cols[i];
          const [x2, ys2] = cols[i + 1];
          ys1.forEach((y1, a) =>
            ys2.forEach((y2, b) =>
              edges.push({ x1, y1, x2, y2, key: `${i}-${a}-${b}` }),
            ),
          );
        }
        return (
          <>
            {edges.map((e, i) => (
              <motion.line
                key={e.key}
                x1={e.x1}
                y1={e.y1}
                x2={e.x2}
                y2={e.y2}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="0.6"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  delay: reduceMotion ? 0 : 0.2 + i * 0.02,
                  ease: "easeOut",
                }}
              />
            ))}
            {cols.flatMap(([x, ys], colIdx) =>
              ys.map((y, i) => (
                <motion.circle
                  key={`n-${colIdx}-${i}`}
                  cx={x}
                  cy={y}
                  r={6}
                  fill={colIdx === cols.length - 1 ? "var(--color-secondary)" : "white"}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: reduceMotion ? 0 : 0.6 + colIdx * 0.15 + i * 0.05,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                />
              )),
            )}
            {/* Traveling pulse along a path */}
            {!reduceMotion && (
              <motion.circle
                r={3}
                fill="var(--color-secondary)"
                initial={{ cx: 40, cy: 100, opacity: 0 }}
                animate={{
                  cx: [40, 100, 160],
                  cy: [100, 80, 70],
                  opacity: [0, 1, 0],
                }}
                transition={{ duration: 1.6, delay: 1.5, repeat: Infinity, repeatDelay: 0.4 }}
              />
            )}
          </>
        );
      })()}
    </svg>
  </SceneShell>
);

const AiScene4: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="04 · Agents & generation"
    title={
      <>
        Predict the{" "}
        <span className="font-italic-serif text-secondary">next move.</span>
      </>
    }
    body="Language models choose the next token. Agents observe, plan, and act inside an environment."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 flex flex-col justify-center gap-3 px-1">
      <div className="font-mono text-[11px] text-background/60">prompt</div>
      <div className="font-mono text-sm text-background/90">
        "An AI agent decides…"
      </div>
      <div className="font-mono text-[11px] text-background/60 mt-2">candidates</div>
      <div className="space-y-1.5">
        {[
          { token: "what", w: 0.41, on: true },
          { token: "to", w: 0.28 },
          { token: "next", w: 0.18 },
          { token: "wisely", w: 0.13 },
        ].map((t, i) => (
          <motion.div
            key={t.token}
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.4 + i * 0.12 }}
          >
            <span
              className={`font-mono text-xs w-16 ${
                t.on ? "text-secondary" : "text-background/70"
              }`}
            >
              {t.token}
            </span>
            <div className="flex-1 h-1.5 bg-background/15 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${t.on ? "bg-secondary" : "bg-background/40"}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: t.w }}
                transition={{
                  duration: 0.7,
                  delay: reduceMotion ? 0 : 0.6 + i * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{ transformOrigin: "left" }}
              />
            </div>
            <span className="w-10 text-right font-mono text-[11px] tabular-nums text-background/60">
              {Math.round(t.w * 100)}%
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  </SceneShell>
);

const AiOutro: React.FC<SceneProps> = ({ reduceMotion }) => (
  <div className="absolute inset-0 flex items-center justify-center px-10 text-center">
    <div>
      <motion.div
        className="font-mono text-[11px] uppercase tracking-[0.3em] text-secondary mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        // 8 weeks · live cohort
      </motion.div>
      <motion.div
        className="font-display text-5xl md:text-7xl font-bold tracking-tight leading-none"
        initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: reduceMotion ? 0 : 0.15, ease: [0.16, 1, 0.3, 1] }}
      >
        All About <span className="font-italic-serif text-secondary">AI.</span>
      </motion.div>
      <motion.div
        className="mt-5 text-background/70 max-w-xl mx-auto text-sm md:text-base"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.55 }}
      >
        Systems thinking, data, models, and agents — taught with pseudocode,
        case studies, and live discussion.
      </motion.div>
    </div>
  </div>
);

// =============================================================
// Python Foundations — 5 scenes
// =============================================================

const PyScene1: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="01 · Hello, Python"
    title={
      <>
        Code that{" "}
        <span className="font-italic-serif text-secondary">talks back.</span>
      </>
    }
    body="Start in the Python REPL. Type one line, run it, see it. Build instinct from your very first session."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 rounded-xl border border-background/15 bg-background/[0.05] p-4 flex flex-col justify-center gap-2">
      <TerminalLine prefix=">>>" text='print("hello, world")' delay={0.3} reduceMotion={reduceMotion} />
      <motion.div
        className="font-mono text-sm text-secondary pl-7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: reduceMotion ? 0 : 1.4 }}
      >
        hello, world
      </motion.div>
      <TerminalLine prefix=">>>" text="2 + 2" delay={1.7} reduceMotion={reduceMotion} />
      <motion.div
        className="font-mono text-sm text-secondary pl-7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: reduceMotion ? 0 : 2.4 }}
      >
        4
      </motion.div>
    </div>
  </SceneShell>
);

const PyScene2: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="02 · Variables & types"
    title={
      <>
        Boxes with{" "}
        <span className="font-italic-serif text-secondary">labels.</span>
      </>
    }
    body="Numbers, strings, lists, dicts — give them names and combine them to model anything."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 grid grid-cols-2 gap-3 p-1">
      {[
        { name: "name", value: '"Ada"', tone: "text-secondary" },
        { name: "age", value: "16", tone: "text-secondary" },
        { name: "skills", value: '["py", "ml"]', tone: "text-secondary" },
        { name: "score", value: "0.92", tone: "text-secondary" },
      ].map((v, i) => (
        <motion.div
          key={v.name}
          className="rounded-lg border border-background/15 bg-background/[0.04] p-3 font-mono text-xs"
          initial={{ opacity: 0, y: 16, scale: reduceMotion ? 1 : 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.45,
            delay: reduceMotion ? 0 : 0.3 + i * 0.12,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <div className="text-background/55">{v.name}</div>
          <div className={`mt-1 text-base ${v.tone}`}>{v.value}</div>
        </motion.div>
      ))}
    </div>
  </SceneShell>
);

const PyScene3: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="03 · Loops & logic"
    title={
      <>
        Make the computer{" "}
        <span className="font-italic-serif text-secondary">repeat.</span>
      </>
    }
    body="for-loops, if-statements, comprehensions — the patterns you'll reach for in every project."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 rounded-xl border border-background/15 bg-background/[0.05] p-4 font-mono text-xs leading-relaxed">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.2 }}
      >
        <span className="text-secondary">for</span> n{" "}
        <span className="text-secondary">in</span> [1, 2, 3, 4, 5]:
      </motion.div>
      <div className="pl-4 space-y-1 mt-1">
        {[1, 2, 3, 4, 5].map((n, i) => (
          <motion.div
            key={n}
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.3,
              delay: reduceMotion ? 0 : 0.6 + i * 0.18,
            }}
          >
            <span className="text-background/55">print(n * n)</span>
            <span className="text-background/40">→</span>
            <span className="text-secondary">{n * n}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </SceneShell>
);

const PyScene4: React.FC<SceneProps> = ({ reduceMotion }) => (
  <SceneShell
    kicker="04 · Functions & projects"
    title={
      <>
        Wrap ideas into{" "}
        <span className="font-italic-serif text-secondary">tools.</span>
      </>
    }
    body="Build small functions, compose them into real things — a quiz bot, a number game, a tiny website."
    reduceMotion={reduceMotion}
  >
    <div className="absolute inset-0 rounded-xl border border-background/15 bg-background/[0.05] p-4 font-mono text-xs leading-relaxed">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.2 }}
      >
        <span className="text-secondary">def</span> greet(name):
      </motion.div>
      <motion.div
        className="pl-4 text-background/80"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.55 }}
      >
        <span className="text-secondary">return</span> <span>f"hi, {"{name}"}!"</span>
      </motion.div>
      <motion.div
        className="mt-3 text-background/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: reduceMotion ? 0 : 1.0 }}
      >
        &gt;&gt;&gt; greet("class")
      </motion.div>
      <motion.div
        className="text-secondary"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: reduceMotion ? 0 : 1.4 }}
      >
        "hi, class!"
      </motion.div>
    </div>
  </SceneShell>
);

const PyOutro: React.FC<SceneProps> = ({ reduceMotion }) => (
  <div className="absolute inset-0 flex items-center justify-center px-10 text-center">
    <div>
      <motion.div
        className="font-mono text-[11px] uppercase tracking-[0.3em] text-secondary mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        // 8 weeks · live cohort
      </motion.div>
      <motion.div
        className="font-display text-5xl md:text-7xl font-bold tracking-tight leading-none"
        initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: reduceMotion ? 0 : 0.15, ease: [0.16, 1, 0.3, 1] }}
      >
        Python <span className="font-italic-serif text-secondary">Foundations.</span>
      </motion.div>
      <motion.div
        className="mt-5 text-background/70 max-w-xl mx-auto text-sm md:text-base"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.55 }}
      >
        Eight live sessions to go from your first <span className="font-mono">print()</span> to
        functions, loops, and your first project.
      </motion.div>
    </div>
  </div>
);

export const SCRIPTS: Record<string, ExplainerScript> = {
  "all-about-ai": {
    kicker: "// all about ai",
    aria:
      "Animated explainer for the All About AI course, cycling through how AI systems differ from traditional code, how data is represented, how neural networks work, and how agents and generative models predict.",
    scenes: [
      { render: AiScene1, durationMs: 4200 },
      { render: AiScene2, durationMs: 3800 },
      { render: AiScene3, durationMs: 4400 },
      { render: AiScene4, durationMs: 4200 },
      { render: AiOutro, durationMs: 4000 },
    ],
  },
  "python-foundations-live": {
    kicker: "// python foundations",
    aria:
      "Animated explainer for the Python Foundations live cohort, walking through the REPL, variables, loops, and functions before landing on a course lockup.",
    scenes: [
      { render: PyScene1, durationMs: 4400 },
      { render: PyScene2, durationMs: 3800 },
      { render: PyScene3, durationMs: 4200 },
      { render: PyScene4, durationMs: 4200 },
      { render: PyOutro, durationMs: 4000 },
    ],
  },
};
